import {
  ArrowRight,
  Check,
  Droplet,
  Droplets,
  Eraser,
  Minus,
  Scale,
  Smile,
  Snowflake,
  Sparkles,
  Stethoscope,
  Sun,
  Syringe,
  Waves,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { Head } from 'vite-react-ssg'
import { ProofStrip } from '@/components/CasablancaProof'
import { ClientMediaReviews } from '@/components/ClientMediaReviews'
import { EditorialFaq } from '@/components/EditorialFaq'
import { MetaPartnerBadge } from '@/components/MetaPartnerBadge'
import { TrustedClinicsMarquee } from '@/components/TrustedClinics'
import {
  AESTHETIC_CLINIC_MARKETING,
  AESTHETIC_DOCTOR_MARKETING,
  MEDICAL_MARKETING_AGENCY,
  SLIMMING_CENTER_MARKETING,
  type AiServiceLandingPage,
} from '@/data/aiServiceLandingPages'
import { CABINET_PILOTE, CABINET_PILOTE_COPY } from '@/data/cabinetPiloteOffer'
import { getResourceArticle, resourceArticlePath, RESOURCES } from '@/data/resources'
import { CASABLANCA_CASE_PATH, ORGANIZATION_ID, SITE_URL } from '@/data/seoConstants'
import { AlternateHead } from '@/i18n/AlternateHead'
import { aboutCopy } from '@/i18n/pages'

const ICONS: Record<string, LucideIcon> = {
  droplet: Droplet,
  droplets: Droplets,
  eraser: Eraser,
  scale: Scale,
  smile: Smile,
  snowflake: Snowflake,
  sparkles: Sparkles,
  stethoscope: Stethoscope,
  sun: Sun,
  syringe: Syringe,
  waves: Waves,
  zap: Zap,
}

const offer = CABINET_PILOTE_COPY.fr
const roles = aboutCopy.fr.roles

// Omar's and Bilal's photos are full-length shots: anchor the square crop on the face.
const TEAM: { name: string; role: string; image: string; focus?: string }[] = [
  { name: 'Dr Khalil Jarrar', role: 'Cofondateur', image: '/images/team/khalil-192.webp' },
  { name: 'Mohamed Amine Saada', role: roles.amine, image: '/images/team/mohamed-amine-saada-192.webp' },
  { name: 'Omar Jarrar', role: roles.omar, image: '/images/team/team-member-2.webp', focus: '50% 8%' },
  { name: 'Bilal Jarrar', role: roles.bilal, image: '/images/team/team-member-3.webp', focus: '50% 8%' },
]

const STEPS = [
  { title: 'Publicité ciblée', body: 'Une campagne par traitement, sur Facebook et Instagram.', owner: 'Notre équipe' },
  { title: 'Réponse immédiate', body: 'L’agent IA répond sur WhatsApp en quelques instants, 24h/24.', owner: 'Agent IA' },
  { title: 'Qualification', body: 'Traitement, ville, disponibilité. Les demandes hors cible sont écartées.', owner: 'Agent IA' },
  { title: 'Appel et rendez-vous', body: 'Un appel court, et le créneau est inscrit dans votre agenda.', owner: 'Notre équipe' },
  { title: 'Suivi jusqu’à la venue', body: 'Confirmation, rappels et reprogrammation des absences.', owner: 'Notre équipe' },
]

const eyebrow = 'font-fwmono text-[11px] font-medium uppercase tracking-[0.16em] text-fw-muted'
const h2 = 'text-3xl font-bold leading-[1.1] tracking-[-0.03em] text-fw-ink md:text-[44px]'

function TrialLink({ url, light = false, className = '' }: { url: string; light?: boolean; className?: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-xl px-6 py-3 text-[15px] font-semibold ${
        light ? 'bg-white text-fw-ink hover:bg-fw-offwhite' : 'bg-fw-violet text-white hover:bg-fw-violet-hover'
      } ${className}`}
    >
      Démarrer l’essai gratuit de {CABINET_PILOTE.trialDays} jours <ArrowRight className="size-4" aria-hidden />
    </a>
  )
}

function ProductShot({
  name,
  alt,
  caption,
  height,
  eager = false,
}: {
  name: string
  alt: string
  caption: string
  /** Intrinsic height of the 1600px-wide export, to reserve space. */
  height: number
  eager?: boolean
}) {
  const base = `/media/product/${name}`
  return (
    <figure>
      <div className="overflow-hidden rounded-2xl border border-fw-hairline bg-fw-offwhite p-1.5 shadow-[0_24px_60px_-28px_rgba(10,28,53,0.35)] md:p-2">
        <img
          src={`${base}-1600.webp`}
          srcSet={`${base}-800.webp 800w, ${base}-1600.webp 1600w`}
          sizes="(min-width: 1280px) 1180px, 100vw"
          alt={alt}
          width="1600"
          height={height}
          loading={eager ? 'eager' : 'lazy'}
          fetchPriority={eager ? 'high' : undefined}
          className="block h-auto w-full rounded-xl"
        />
      </div>
      <figcaption className="mt-3 text-sm text-fw-muted">{caption}</figcaption>
    </figure>
  )
}

function AcquisitionLanding({ page }: { page: AiServiceLandingPage }) {
  const canonicalPath = `/fr/services/${page.slug}`
  const canonical = `${SITE_URL}${canonicalPath}`
  const trialUrl = `${CABINET_PILOTE.whatsappUrl}?text=${encodeURIComponent(page.offer?.whatsappMessage ?? '')}`
  const related = page.relatedSlugs
    .map((slug) => getResourceArticle(slug, 'fr'))
    .filter((article): article is (typeof RESOURCES)[number] => Boolean(article))

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${canonical}#service`,
      name: page.serviceName,
      description: page.serviceDescription,
      url: canonical,
      provider: { '@id': ORGANIZATION_ID },
      areaServed: { '@type': 'Country', name: 'Morocco' },
      availableLanguage: ['fr', 'ar', 'ary'],
      serviceType: page.serviceType,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${SITE_URL}/fr` },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE_URL}/fr/services` },
        { '@type': 'ListItem', position: 3, name: page.serviceName, item: canonical },
      ],
    },
  ]

  return (
    <main className="fw-editorial min-h-screen bg-fw-white">
      <AlternateHead title={page.title} description={page.metaDescription} canonicalPath={canonicalPath} />
      <Head>
        <meta property="og:image" content={`${SITE_URL}/og-image.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preload" as="image" href="/media/product/demo-kanban-conversations-1600.webp" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Head>

      <section className="mx-auto max-w-[1240px] px-6 pb-14 pt-28 md:px-12 md:pb-20 md:pt-36">
        <nav aria-label="Fil d’Ariane" className="flex flex-wrap items-center gap-2 font-fwmono text-[11px] uppercase tracking-[0.12em] text-fw-muted">
          <Link to="/fr" className="hover:text-fw-ink">Accueil</Link>
          <span aria-hidden>/</span>
          <Link to="/fr/services" className="hover:text-fw-ink">Services</Link>
          <span aria-hidden>/</span>
          <span aria-current="page" className="text-fw-ink">{page.breadcrumbLabel}</span>
        </nav>

        <p className={`mt-10 ${eyebrow}`}>{page.eyebrow}</p>
        <h1 className="mt-5 max-w-[1040px] text-[38px] font-extrabold leading-[1.03] tracking-[-0.045em] text-fw-ink md:text-[66px]">
          {page.h1}
        </h1>
        <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:items-start lg:gap-16">
          <p className="max-w-2xl text-[17px] leading-relaxed text-fw-graphite md:text-[19px]">{page.lede}</p>
          <div className="lg:justify-self-end">
            <div className="flex flex-col gap-3 sm:flex-row [&>*]:whitespace-nowrap">
              <TrialLink url={trialUrl} />
              <Link
                to="/discover#scheduler"
                className="inline-flex min-h-12 items-center justify-center rounded-xl border border-fw-hairline px-6 py-3 text-[15px] font-semibold text-fw-ink hover:border-fw-ink"
              >
                Réserver un appel
              </Link>
            </div>
            <p className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-fw-muted">
              <span>{offer.trial}</span>
              <span aria-hidden>·</span>
              <span>{offer.commitment}</span>
            </p>
            <MetaPartnerBadge label={offer.metaPartner} className="mt-3" />
          </div>
        </div>

        <div className="mt-14 md:mt-16">
          <ProductShot
            name="demo-kanban-conversations"
            alt="Tableau des conversations de la plateforme Forteuno : demandes escaladées à l’équipe, en attente, à relancer et gérées par l’agent IA"
            caption="La plateforme Forteuno : chaque conversation est classée, de l’agent IA jusqu’à votre équipe. Données de démonstration anonymisées."
            height={692}
            eager
          />
        </div>
      </section>

      <TrustedClinicsMarquee locale="fr" />

      <section className="mx-auto max-w-[1240px] px-6 py-20 md:px-12 md:py-28" aria-labelledby="steps-heading">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:gap-16">
          <div>
            <p className={eyebrow}>Le système</p>
            <h2 id="steps-heading" className={`mt-4 ${h2}`}>De la publicité au patient en salle d’attente</h2>
          </div>
          <p className="text-[17px] leading-relaxed text-fw-graphite lg:pt-10">
            Cinq étapes, prises en charge de bout en bout. Votre rôle se limite à valider les annonces et à accueillir les
            patients qui arrivent.
          </p>
        </div>
        <ol className="mt-14 grid border-t border-fw-ink md:grid-cols-5">
          {STEPS.map((step, index) => (
            <li key={step.title} className="border-b border-fw-hairline py-6 md:border-b-0 md:border-r md:px-5 md:py-8 md:first:pl-0 md:last:border-r-0">
              <p className="font-fwmono text-[13px] font-medium text-fw-muted">0{index + 1}</p>
              <h3 className="mt-4 text-lg font-bold tracking-[-0.01em] text-fw-ink">{step.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-fw-graphite">{step.body}</p>
              <p className="mt-4 text-xs font-semibold uppercase tracking-[0.12em] text-fw-violet">{step.owner}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="border-y border-fw-hairline bg-fw-offwhite" aria-labelledby="handoff-heading">
        <div className="mx-auto max-w-[1240px] px-6 py-20 md:px-12 md:py-28">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:gap-16">
            <div>
              <p className={eyebrow}>La plateforme</p>
              <h2 id="handoff-heading" className={`mt-4 ${h2}`}>Quand l’IA passe la main, une personne reprend</h2>
            </div>
            <p className="text-[17px] leading-relaxed text-fw-graphite lg:pt-10">
              Chaque conversation reste visible pour votre équipe et la nôtre. Quand l’agent IA atteint une limite, il se met
              en pause et une personne reprend, avec tout l’historique sous les yeux.
            </p>
          </div>
          <div className="mt-12">
            <ProductShot
              name="demo-inbox-reprise-humaine"
              alt="Boîte de réception de la plateforme Forteuno : conversation WhatsApp en pause, reprise par un membre de l’équipe, avec la fiche patient"
              caption="Reprise humaine : le bot se met en pause et l’équipe répond, avec tout l’historique. Données anonymisées."
              height={711}
            />
          </div>
          <dl className="mt-14 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {page.deliverables.map((item) => (
              <div key={item.title} className="border-t border-fw-ink pt-5">
                <dt className="text-base font-bold text-fw-ink">{item.title}</dt>
                <dd className="mt-2 text-[15px] leading-relaxed text-fw-graphite">{item.body}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {page.pains && page.gains ? (
        <section className="mx-auto max-w-[1240px] px-6 py-20 md:px-12 md:py-28" aria-labelledby="compare-heading">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:gap-16">
            <div>
              <p className={eyebrow}>Le constat</p>
              <h2 id="compare-heading" className={`mt-4 ${h2}`}>{page.introHeading}</h2>
            </div>
            <div className="space-y-5 text-[17px] leading-relaxed text-fw-graphite">
              {page.intro.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
          <div className="mt-14 grid border-t border-fw-ink md:grid-cols-2">
            {[
              { label: 'Sans système', items: page.pains, Icon: Minus, tone: 'text-fw-muted' },
              { label: 'Avec Forteuno Web', items: page.gains, Icon: Check, tone: 'text-fw-violet' },
            ].map((column, columnIndex) => (
              <div key={column.label} className={columnIndex === 1 ? 'md:border-l md:border-fw-hairline md:pl-10' : 'md:pr-10'}>
                <p className="pt-6 text-sm font-bold uppercase tracking-[0.12em] text-fw-ink">{column.label}</p>
                <ul className="mt-2 divide-y divide-fw-hairline">
                  {column.items.map((item) => (
                    <li key={item} className="flex gap-3 py-4 text-base leading-relaxed text-fw-graphite">
                      <column.Icon className={`mt-1 size-4 shrink-0 ${column.tone}`} aria-hidden />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="border-t border-fw-hairline" aria-labelledby="treatments-heading">
        <div className="mx-auto max-w-[1240px] px-6 py-20 md:px-12 md:py-28">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:gap-16">
            <div>
              <p className={eyebrow}>Traitements</p>
              <h2 id="treatments-heading" className={`mt-4 ${h2}`}>{page.useCasesHeading}</h2>
            </div>
            <p className="text-[17px] leading-relaxed text-fw-graphite lg:pt-10">{page.useCasesIntro}</p>
          </div>
          <div className="mt-14 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
            {page.useCases.map((item, index) => {
              const Icon = ICONS[page.useCaseIcons?.[index] ?? ''] ?? Sparkles
              return (
                <article key={item.title} className="border-t border-fw-hairline pt-6">
                  <Icon className="size-6 text-fw-ink" strokeWidth={1.5} aria-hidden />
                  <h3 className="mt-5 text-lg font-bold tracking-[-0.01em] text-fw-ink">{item.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-fw-graphite">{item.body}</p>
                </article>
              )
            })}
          </div>
        </div>
      </section>

      {page.offer ? (
        <section className="px-6 md:px-12" aria-labelledby="offer-heading">
          <div className="mx-auto grid max-w-[1240px] gap-10 rounded-3xl bg-fw-ink px-7 py-12 text-white md:px-14 md:py-16 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-16">
            <div>
              <p className="font-fwmono text-[11px] font-medium uppercase tracking-[0.16em] text-white/55">{offer.trial}</p>
              <h2 id="offer-heading" className="mt-4 text-3xl font-bold leading-[1.1] tracking-[-0.03em] md:text-[44px]">
                {page.offer.heading}
              </h2>
              <TrialLink url={trialUrl} light className="mt-8" />
            </div>
            <div>
              <p className="text-[17px] leading-relaxed text-white/75">{page.offer.body}</p>
              <ul className="mt-8 divide-y divide-white/10 border-y border-white/10">
                {page.offer.points.map((point) => (
                  <li key={point} className="flex gap-3 py-4 text-[15px] leading-relaxed text-white/85">
                    <Check className="mt-1 size-4 shrink-0 text-white/50" aria-hidden />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ) : null}

      <section className="mt-20 border-y border-fw-hairline bg-fw-offwhite md:mt-28" aria-labelledby="proof-heading">
        <div className="mx-auto max-w-[1240px] px-6 pt-20 md:px-12 md:pt-24">
          <p className={eyebrow}>Résultats réels</p>
          <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <h2 id="proof-heading" className={`max-w-3xl ${h2}`}>Le même système, déjà en place dans une clinique à Casablanca</h2>
            <Link to={CASABLANCA_CASE_PATH} className="inline-flex min-h-11 shrink-0 items-center gap-2 font-semibold text-fw-violet hover:text-fw-violet-hover">
              Lire l’étude de cas <ArrowRight className="size-4" aria-hidden />
            </Link>
          </div>
        </div>
        <div className="mt-10">
          <ProofStrip locale="fr" />
        </div>
        <ClientMediaReviews />
      </section>

      <section className="mx-auto max-w-[1240px] px-6 py-20 md:px-12 md:py-28" aria-labelledby="team-heading">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] lg:items-center lg:gap-16">
          <div>
            <p className={eyebrow}>L’équipe</p>
            <h2 id="team-heading" className={`mt-4 ${h2}`}>Des personnes réelles derrière chaque appel</h2>
            <p className="mt-5 text-[17px] leading-relaxed text-fw-graphite">
              L’IA répond et qualifie. Les appels, le suivi et les ajustements de vos campagnes restent entre les mains de
              notre équipe, que vous pouvez joindre directement.
            </p>
            <Link to="/fr/about" className="mt-6 inline-flex min-h-11 items-center gap-2 font-semibold text-fw-violet hover:text-fw-violet-hover">
              Découvrir l’équipe <ArrowRight className="size-4" aria-hidden />
            </Link>
          </div>
          <ul className="grid grid-cols-2 gap-6 sm:grid-cols-4">
            {TEAM.map((member) => (
              <li key={member.name}>
                <div className="aspect-[4/5] w-full overflow-hidden rounded-xl bg-fw-offwhite">
                  <img
                    src={member.image}
                    alt={member.name}
                    width="192"
                    height="240"
                    loading="lazy"
                    className="size-full object-cover"
                    style={member.focus ? { objectPosition: member.focus, transform: 'scale(1.5)', transformOrigin: member.focus } : undefined}
                  />
                </div>
                <p className="mt-3 text-sm font-bold text-fw-ink">{member.name}</p>
                <p className="mt-0.5 text-xs leading-snug text-fw-muted">{member.role}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-fw-hairline" aria-labelledby="limits-heading">
        <div className="mx-auto grid max-w-[1240px] gap-10 px-6 py-20 md:px-12 md:py-24 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:gap-16">
          <div>
            <p className={eyebrow}>Conformité</p>
            <h2 id="limits-heading" className={`mt-4 ${h2}`}>{page.limitsHeading}</h2>
          </div>
          <ul className="divide-y divide-fw-hairline border-y border-fw-hairline">
            {page.limits.map((limit) => (
              <li key={limit} className="flex gap-3 py-4 text-base leading-relaxed text-fw-graphite">
                <Check className="mt-1 size-4 shrink-0 text-fw-ink" aria-hidden />
                {limit}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-[980px] px-6 pb-20 md:px-12 md:pb-28">
        <EditorialFaq heading="Questions fréquentes" items={page.faqs} />
      </section>

      {related.length ? (
        <section className="border-y border-fw-hairline bg-fw-offwhite">
          <div className="mx-auto max-w-[1240px] px-6 py-20 md:px-12">
            <p className={eyebrow}>Pour approfondir</p>
            <h2 className={`mt-4 ${h2}`}>Guides liés</h2>
            <div className="mt-10 grid border-t border-fw-ink md:grid-cols-3">
              {related.map((article, index) => (
                <Link
                  key={article.slug}
                  to={resourceArticlePath(article)}
                  className={`group block border-b border-fw-hairline py-6 md:border-b-0 md:px-6 md:py-8 ${index === 0 ? 'md:pl-0' : 'md:border-l'}`}
                >
                  <h3 className="text-lg font-bold leading-snug text-fw-ink group-hover:text-fw-violet">{article.title}</h3>
                  <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-fw-graphite">{article.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-fw-violet">
                    Lire le guide <ArrowRight className="size-4" aria-hidden />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="mx-auto max-w-[1240px] px-6 py-16 md:px-12 md:py-20">
        <p className={eyebrow}>{page.sibling.eyebrow}</p>
        <Link to={page.sibling.href} className="group mt-4 flex items-start justify-between gap-6 border-y border-fw-hairline py-8">
          <div>
            <h2 className="text-2xl font-bold tracking-[-0.02em] text-fw-ink group-hover:text-fw-violet md:text-3xl">{page.sibling.title}</h2>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-fw-graphite">{page.sibling.body}</p>
          </div>
          <ArrowRight className="mt-1 size-6 shrink-0 text-fw-ink transition-transform group-hover:translate-x-1" aria-hidden />
        </Link>
        {page.crossLinks?.length ? (
          <nav aria-label="Autres spécialités" className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-[15px] font-semibold">
            {page.crossLinks.map((link) => (
              <Link key={link.href} to={link.href} className="text-fw-ink underline decoration-fw-hairline underline-offset-4 hover:text-fw-violet hover:decoration-fw-violet">
                {link.label}
              </Link>
            ))}
          </nav>
        ) : null}
      </section>

      <section className="bg-fw-ink px-6 py-20 text-white md:px-12 md:py-28">
        <div className="mx-auto flex max-w-[1240px] flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="font-fwmono text-[11px] font-medium uppercase tracking-[0.16em] text-white/55">Prochaine étape</p>
            <h2 className="mt-4 text-3xl font-bold leading-[1.1] tracking-[-0.03em] md:text-[48px]">{page.closing?.heading}</h2>
            <p className="mt-5 text-[17px] leading-relaxed text-white/70">{page.closing?.body}</p>
          </div>
          <TrialLink url={trialUrl} light className="shrink-0" />
        </div>
      </section>
    </main>
  )
}

export function MedicalMarketingAgency() {
  return <AcquisitionLanding page={MEDICAL_MARKETING_AGENCY} />
}

export function SlimmingCenterMarketing() {
  return <AcquisitionLanding page={SLIMMING_CENTER_MARKETING} />
}

export function AestheticDoctorMarketing() {
  return <AcquisitionLanding page={AESTHETIC_DOCTOR_MARKETING} />
}

export function AestheticClinicMarketing() {
  return <AcquisitionLanding page={AESTHETIC_CLINIC_MARKETING} />
}
